import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { adminNav } from '@/components/px/nav'
import { AuditTable } from '@/features/audit/components/AuditTable'
import {
  auditRetryClicked,
  exportAudit,
  fetchAuditPage,
  fetchMoreAudit
} from '@/features/audit/auditSlice'
import type { AuditFilters } from '@/features/audit/types'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { useAppDispatch, useAppSelector } from '@/store'

const inputClass = 'rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground'

/** Only non-blank fields travel — blank is the server's "no clause". */
const clean = (filters: AuditFilters): AuditFilters => ({
  action: filters.action?.trim() || undefined,
  entityType: filters.entityType?.trim() || undefined,
  actorUserId: filters.actorUserId?.trim() || undefined
})

/**
 * The audit log (`/admin/audit`, PX-W6.2): filters + cursor pagination + the
 * export affordance. Filter changes reset the list (the canonical filter
 * string is the cache key); Load more appends the next page.
 */
export default function AuditLogPage() {
  const { t } = useTranslation(['audit', 'people'])
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()

  const items = useAppSelector((s) => s.audit.items)
  const nextCursor = useAppSelector((s) => s.audit.nextCursor)
  const cache = useAppSelector((s) => s.audit.cache)
  const error = useAppSelector((s) => s.audit.error)
  const loadingMore = useAppSelector((s) => s.audit.loadingMore)
  const exporting = useAppSelector((s) => s.audit.exporting)
  const exportError = useAppSelector((s) => s.audit.exportError)

  const [action, setAction] = useState('')
  const [entityType, setEntityType] = useState('')
  const [actorUserId, setActorUserId] = useState('')

  useEffect(() => {
    void dispatch(fetchAuditPage(clean({ action, entityType, actorUserId })))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filters are the effect's own state; remount re-applies
  }, [dispatch])

  const onApplyClicked = (): void => {
    const filters = clean({ action, entityType, actorUserId })
    void dispatch(fetchAuditPage(filters))
  }

  const onLoadMoreClicked = (): void => {
    void dispatch(fetchMoreAudit())
  }

  const onExportClicked = async (): Promise<void> => {
    try {
      const blob = await dispatch(exportAudit()).unwrap()
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'audit-export.json'
      document.body.append(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    } catch {
      // The mapped exportError renders below.
    }
  }

  return (
    <AppShell
      moduleLabel={t('people:title')}
      breadcrumb={<span>{t('audit:title')}</span>}
      contextPill={t('people:chrome.contextPill')}
      nav={adminNav('admin')}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('audit:title')}</h1>
        <p className="text-sm text-muted-foreground">{t('audit:lede')}</p>
      </header>

      <section aria-label={t('audit:title')} className="mt-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm text-ink-soft">
          {t('audit:filters.action')}
          <input
            type="text"
            value={action}
            onChange={(event) => setAction(event.target.value)}
            className={`${inputClass} w-44`}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-ink-soft">
          {t('audit:filters.entityType')}
          <input
            type="text"
            value={entityType}
            onChange={(event) => setEntityType(event.target.value)}
            className={`${inputClass} w-44`}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-ink-soft">
          {t('audit:filters.actor')}
          <input
            type="text"
            value={actorUserId}
            onChange={(event) => setActorUserId(event.target.value)}
            className={`${inputClass} w-52 font-mono text-xs`}
          />
        </label>
        <button
          type="button"
          onClick={onApplyClicked}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t('audit:filters.apply')}
        </button>
        <button
          type="button"
          onClick={() => void onExportClicked()}
          disabled={exporting}
          className="ml-auto rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted disabled:opacity-60"
        >
          {exporting ? t('audit:exporting') : t('audit:export')}
        </button>
      </section>

      {exportError !== null && (
        <p role="alert" className="mt-2 text-sm text-blocked">
          {toMessage(exportError)}
        </p>
      )}

      <section className="mt-4 rounded-lg border border-border bg-card">
        {cache.loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          <div className="p-4">
            <ForbiddenPanel requested="/{orgId}/audit" />
          </div>
        ) : error !== null ? (
          <div className="p-4">
            <ErrorPanel
              error={error}
              onRetryClicked={() => {
                dispatch(auditRetryClicked())
                void dispatch(fetchAuditPage(clean({ action, entityType, actorUserId })))
              }}
            />
          </div>
        ) : items.length === 0 ? (
          <EmptyPanel body={t('audit:empty')} />
        ) : (
          <AuditTable rows={items} />
        )}
      </section>

      {nextCursor !== null && (
        <div className="mt-4">
          <button
            type="button"
            onClick={onLoadMoreClicked}
            disabled={loadingMore}
            className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted disabled:opacity-60"
          >
            {loadingMore ? t('audit:loadingMore') : t('audit:loadMore')}
          </button>
        </div>
      )}
    </AppShell>
  )
}
